import type { Application } from 'pixi.js';

export interface LetterboxInfo {
  /** CSS width of the rendered canvas in px. */
  displayW: number;
  /** CSS height of the rendered canvas in px. */
  displayH: number;
  /** Scale factor applied to the design-space to reach CSS pixels. */
  scale: number;
  /** How much of the viewport spills past each side of the canvas, in design-space px. */
  bleedX: number;
  /** Same, vertical. */
  bleedY: number;
}

type FitListener = (info: LetterboxInfo) => void;

const listeners: FitListener[] = [];
let lastInfo: LetterboxInfo | null = null;

/** Subscribe to every resize. Called immediately with the current size. */
export function onLetterboxFit(listener: FitListener): () => void {
  listeners.push(listener);
  if (lastInfo) listener(lastInfo);
  return () => {
    const i = listeners.indexOf(listener);
    if (i >= 0) listeners.splice(i, 1);
  };
}

/** Current letterbox info (undefined until install). */
export function getLetterboxInfo(): LetterboxInfo | null {
  return lastInfo;
}

/**
 * Letterbox fit: canvas keeps its design aspect ratio. The renderer buffer
 * stays at design resolution. We also compute how much "bleed" the viewport
 * has past the canvas on each axis, in design-space pixels — scenes use this
 * to extend the background layer and hide the letterbox bars.
 */
export function installLetterboxFit(
  app: Application,
  designWidth: number,
  designHeight: number
): void {
  const canvas = app.canvas;
  canvas.style.position = 'absolute';
  canvas.style.transformOrigin = '0 0';

  const host = canvas.parentElement as HTMLElement | null;

  const fit = (): void => {
    const hostRect = host?.getBoundingClientRect();
    const availW = hostRect ? hostRect.width : window.innerWidth;
    const availH = hostRect ? hostRect.height : window.innerHeight;

    // Letterbox: smaller of the two scales, so nothing spills past the edge.
    const scale = Math.min(availW / designWidth, availH / designHeight);
    const displayW = Math.floor(designWidth * scale);
    const displayH = Math.floor(designHeight * scale);

    canvas.style.width = `${displayW}px`;
    canvas.style.height = `${displayH}px`;
    canvas.style.left = `${Math.floor((availW - displayW) / 2)}px`;
    canvas.style.top = `${Math.floor((availH - displayH) / 2)}px`;

    // How many design-space pixels spill past each edge of the canvas.
    // (availW / scale) is viewport width in design-space; subtract design
    // width and split between the two sides.
    const bleedX = Math.max(0, Math.ceil((availW / scale - designWidth) / 2));
    const bleedY = Math.max(0, Math.ceil((availH / scale - designHeight) / 2));

    lastInfo = { displayW, displayH, scale, bleedX, bleedY };
    listeners.forEach((l) => l(lastInfo!));
  };

  app.renderer.resize(designWidth, designHeight);
  fit();
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);

  if (host && 'ResizeObserver' in window) {
    const ro = new ResizeObserver(() => fit());
    ro.observe(host);
  }
}
